
import React, { useMemo } from 'react';
import { useEmailBuilderStore } from '@/store/emailBuilderStore';
import { BlockRenderer } from './BlockRenderer';
import { DragGhostPlaceholder } from './DragGhostPlaceholder';
import { FormattingToolbar } from './FormattingToolbar';
import { useDragState } from './DndProvider';
import type { DocumentConfiguration } from '@/types/email';

export function Canvas() {
  const { document, blockTree, selectBlock } = useEmailBuilderStore();
  const { dropTarget, activeData } = useDragState();

  // Parse document configuration with defaults
  const documentConfig = useMemo<DocumentConfiguration>(() => {
    if (!document) {
      return {
        width: 600,
        alignment: 'center',
        backgroundColor: '#ffffff',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        language: 'en',
      };
    }
    
    try {
      const parsed = document.configuration
        ? (typeof document.configuration === 'string'
          ? JSON.parse(document.configuration)
          : document.configuration)
        : {};
      
      const config = {
        width: parsed.width ?? 600,
        alignment: parsed.alignment ?? 'center',
        canvasBackgroundColor: parsed.canvasBackgroundColor,
        backgroundColor: parsed.backgroundColor ?? '#ffffff',
        backgroundImage: parsed.backgroundImage,
        backgroundRepeat: parsed.backgroundRepeat ?? 'no-repeat',
        backgroundPosition: parsed.backgroundPosition ?? 'center',
        padding: parsed.padding ?? { top: 0, right: 0, bottom: 0, left: 0 },
        language: parsed.language ?? 'en',
      };

      return config;
    } catch (e) {
      console.error('Failed to parse document configuration:', e);
      return {
        width: 600,
        alignment: 'center',
        backgroundColor: '#ffffff',
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'center',
        padding: { top: 0, right: 0, bottom: 0, left: 0 },
        language: 'en',
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [document?.id, document?.configuration]);

  if (!document) {
    return (
      <div className="lw-canvas-empty-state flex h-full items-center justify-center bg-muted">
        <div className="text-center">
          <h3 className="text-lg font-medium text-foreground">No document loaded</h3>
          <p className="mt-2 text-sm text-muted-foreground">
            Select a template or create a new email to get started.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="lw-canvas h-full flex flex-col overflow-hidden">
      {/* Formatting Toolbar - Always visible at top */}
      <FormattingToolbar />

      {/* Canvas Area - Scrollable, background around email (mj-body equivalent) */}
      <div
        className="lw-canvas-area flex-1 overflow-auto bg-muted p-8"
        style={documentConfig.canvasBackgroundColor ? { backgroundColor: documentConfig.canvasBackgroundColor } : undefined}
        onClick={(e) => {
          // Click on canvas background (gray area outside email) shows document properties
          if (e.target === e.currentTarget) {
            selectBlock(null);
          }
        }}
      >
        {/* Email container wrapper for alignment */}
        <div
          className="lw-canvas-email-wrapper w-full"
          style={{
            display: 'flex',
            justifyContent: documentConfig.alignment,
          }}
          onClick={(e) => {
            // Check if we clicked on the wrapper or email container, but not on a block
            const target = e.target as HTMLElement;
            const clickedOnBlock = target.closest('[data-block-id]');
            
            // If we didn't click on a block, show document properties
            if (!clickedOnBlock) {
              selectBlock(null);
            }
          }}
        >
          {/* Email container - mimics email client, applies document background */}
          <div
            className="lw-canvas-email-container shadow-lg"
            style={{
              width: documentConfig.width,
              maxWidth: '100%',
              backgroundColor: documentConfig.backgroundColor,
              backgroundImage: documentConfig.backgroundImage 
                ? `url(${documentConfig.backgroundImage})` 
                : undefined,
              backgroundRepeat: documentConfig.backgroundRepeat,
              backgroundPosition: documentConfig.backgroundPosition,
              paddingTop: `${documentConfig.padding.top + 20}px`,
              paddingRight: `${documentConfig.padding.right}px`,
              paddingBottom: `${documentConfig.padding.bottom + 20}px`,
              paddingLeft: `${documentConfig.padding.left}px`,
            }}
          >
            {blockTree.length === 0 ? (
              // Empty state
              <div className="p-8">
                {activeData ? (
                  <div className="rounded-lg border-2 border-dashed border-primary/40 bg-primary/5 flex items-center justify-center min-h-36">
                    <p className="text-sm font-medium text-primary/60 select-none">
                      Drop blocks to start building your email
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border-2 border-dashed border-border/60 flex items-center justify-center min-h-36">
                    <p className="text-sm font-medium text-muted-foreground/50 select-none">
                      Drop blocks to start building your email
                    </p>
                  </div>
                )}
              </div>
            ) : (
              // Render block tree with ghost placeholder
              <>
                {blockTree.map((block, index) => (
                  <React.Fragment key={block.id}>
                    {dropTarget?.parentId === null && dropTarget.index === index && (
                      <DragGhostPlaceholder />
                    )}
                    <BlockRenderer
                      block={block}
                      parentId={null}
                      index={index}
                    />
                  </React.Fragment>
                ))}
                {dropTarget?.parentId === null && dropTarget.index === blockTree.length && (
                  <DragGhostPlaceholder />
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
